import React, {useCallback} from "react";
import {Modal, ModalBackdrop, ModalButtons, ModalCloseButton} from "components/Common";
import {IconClose} from "components/Icons";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {
	getActiveContest,
	getActiveContestTranslationCategory,
	getContestCorrectAnswers,
	getContestGameBar,
	getIsGameOverModalOpen,
} from "modules/selectors";
import {toggleGameOverModal, fetchContextualPrompt} from "modules/actions";
import styled from "@emotion/styled";
import {DateTime} from "luxon";
import {useNavigate} from "react-router-dom";
import {Exists} from "components/Exists";
import {Preloader} from "components/Preloader";
import {ShareHandler} from "components/ShareHandler";
import {getDashedValue, getLocallyTranslatedCategory} from "modules/utils/helpers";
import {AdobeAnalyticService} from "modules/utils/Services/AdobeAnalyticService";
import {AdobeTrackActionActions, PlayerStats} from "modules/enums";
import {FEEDBACK_URL} from "modules/constants";

const TopText = styled.p`
	font-family: ${({theme}) => theme.fonts.secondary};
	display: flex;
	justify-content: center;
	align-items: center;
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	line-height: 150%;
	letter-spacing: 0.01em;
	color: ${({theme}) => theme.colors.text.white};
	margin: 48px 0 24px 0;
	gap: 12px;
`;

const VerticalHr = styled.span`
	display: block;
	background: ${({theme}) => theme.colors.text.white};
	width: 1px;
	max-width: 1px;
	min-width: 1px;
	height: 21px;
`;

const TitleBlock = styled.p`
	font-style: normal;
	font-weight: 600;
	font-size: 20px;
	line-height: 24px;
	color: ${({theme}) => theme.colors.text.white};
	text-align: center;

	&.mb {
		margin-bottom: 12px;
	}

	&.mb-2 {
		margin-bottom: 24px;
	}
`;

const TextBlock = styled(TitleBlock)`
	font-size: 18px;
	font-weight: 400;
`;

const ScoredValue = styled.h2`
	font-style: normal;
	font-weight: 600;
	font-size: 200px;
	line-height: 200px;
	letter-spacing: 0.2px;
	color: ${({theme}) => theme.colors.brand.primary};
`;

export const GameOverModal: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {t} = useTranslation();

	const contest = useSelector(getActiveContest);
	const isOpen = useSelector(getIsGameOverModalOpen);
	const categoryText = t(useSelector(getActiveContestTranslationCategory));
	const today = DateTime.now().toFormat("LLL dd");

	const correctAnswers = useSelector(getContestCorrectAnswers);
	const gameBar = useSelector(getContestGameBar);

	const closeModal = useCallback(() => {
		dispatch(toggleGameOverModal(false));
		dispatch(fetchContextualPrompt());

		navigate("/summary");
	}, [dispatch, navigate]);

	const closeButtonHandler = useCallback(() => {
		AdobeAnalyticService.TrackAction({
			"action.name": AdobeTrackActionActions.ResultModalExit,
			"action.element.text": "",
			"action.element.targetUrl": window.location.href + "/summary",
			"action.element.index": 2,
			"gaming.mol.gameResult": correctAnswers || 0,
			"gaming.dd.gameDate": AdobeAnalyticService.getTodayDateForAnalytics(),
			"gaming.mol.category": getLocallyTranslatedCategory(contest?.category as PlayerStats),
		});

		closeModal();
	}, [correctAnswers, contest, closeModal]);

	const analyticHandler = useCallback(
		(type: "share" | "feedback") => {
			AdobeAnalyticService.TrackAction({
				"action.name": AdobeTrackActionActions.ResultModalAction.replace("--X--", type),
				"action.element.text": type,
				"action.element.targetUrl": type === "share" ? window.location.href : FEEDBACK_URL,
				"action.element.index": type === "share" ? 1 : 0,
				"gaming.mol.gameResult": correctAnswers || 0,
				"gaming.dd.gameDate": AdobeAnalyticService.getTodayDateForAnalytics(),
				"gaming.mol.category": getLocallyTranslatedCategory(
					contest?.category as PlayerStats
				),
			});
		},
		[contest, correctAnswers]
	);

	// const feedBackHandler = useCallback(() => {
	// 	analyticHandler("feedback");
	// 	openExternalURL(FEEDBACK_URL);
	// }, [analyticHandler]);

	const shareCallback = useCallback(() => {
		analyticHandler("share");
	}, [analyticHandler]);

	if (!isOpen) {
		return null;
	}

	return (
		<ModalBackdrop>
			<Modal>
				<ModalCloseButton onClick={closeButtonHandler}>
					<IconClose />
				</ModalCloseButton>

				<TopText>
					<span>
						{t("game.common.date")}: {today}
					</span>
					<VerticalHr />
					<span>
						{t("game.common.category")}: {categoryText}
					</span>
				</TopText>

				<TitleBlock>{t("game.gameover.scored")}</TitleBlock>
				<ScoredValue>
					<Exists when={correctAnswers !== null}>
						<span>{correctAnswers}</span>
					</Exists>

					<Exists when={correctAnswers === null}>
						<span>
							<Preloader />
						</span>
					</Exists>
				</ScoredValue>

				<TitleBlock className="mb">
					{t("game.gameover.average", {X: getDashedValue(gameBar?.avgToday)})}
				</TitleBlock>
				<TextBlock className="mb-2">{t("game.gameover.thanks")}</TextBlock>

				<ModalButtons className="big">
					<ShareHandler place="results" shareCallback={shareCallback} />
					{/*<OutlinedButton onClick={feedBackHandler}>*/}
					{/*	{t("modal.game_over.feeback")}*/}
					{/*</OutlinedButton>*/}
				</ModalButtons>
			</Modal>
		</ModalBackdrop>
	);
};
